/**
 * The order the customer approval page draws its product cards in (user,
 * 2026-10-26), chosen with "Sort By: Default / Team Name" (Default was
 * "Style Number", renamed and put first 2026-10-29):
 *
 *   "style"  Default: the work order's own order (sortId) -- as it always was
 *   "team"   team name A-Z (case-insensitive, numbers in number order); ties,
 *            and the products with NO team name, in sortId order -- the
 *            no-team ones at the bottom
 *
 * Returns a new array; the one passed in is not touched.
 */
const bySortId = (a, b) => (Number(a?.sortId) || 0) - (Number(b?.sortId) || 0);
const teamOf = (p) => String(p?.teamName || "").trim();

export const orderProducts = (products, order = "style") => {
  const list = [...(products || [])];
  if (order !== "team") return list.sort(bySortId);
  return list.sort((a, b) => {
    const ta = teamOf(a);
    const tb = teamOf(b);
    if (!ta || !tb) return !ta && !tb ? bySortId(a, b) : ta ? -1 : 1;
    return (
      ta.localeCompare(tb, undefined, { sensitivity: "base", numeric: true }) ||
      bySortId(a, b)
    );
  });
};

// Whether "Sort By" is worth showing: some product has a team name (user,
// 2026-10-26: with none, no heading or tabs, and Style No# order).
export const hasTeamName = (products) => (products || []).some((p) => teamOf(p) !== "");

// Team filter (user, 2026-10-29): NO_TEAM picks the products with no team
// name; "" is every product.
export const NO_TEAM = "__no-team__";

// The team names on the page, once each (case-insensitive), A-Z.
export const teamNames = (products) => {
  const byKey = new Map();
  (products || []).forEach((p) => {
    const t = teamOf(p);
    if (t && !byKey.has(t.toLowerCase())) byKey.set(t.toLowerCase(), t);
  });
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }),
  );
};

// The team filter's choices for the products on screen -- the active status
// tab's, not the whole W-Order's (user, 2026-11-02): [[value, label]], All
// Teams first, "No Team Name" last when some product has none.
export const teamOptions = (products) => [
  ["", "All Teams"],
  ...teamNames(products).map((t) => [t, t]),
  ...((products || []).some((p) => !teamOf(p)) ? [[NO_TEAM, "No Team Name"]] : []),
];

export const filterByTeam = (products, team) => {
  const list = products || [];
  if (!team) return list;
  if (team === NO_TEAM) return list.filter((p) => !teamOf(p));
  const key = team.toLowerCase();
  return list.filter((p) => teamOf(p).toLowerCase() === key);
};

export default orderProducts;
